// Vite alias target for `next/navigation` imports inside @edemics/components.
// Adapts Next's app-router hooks to react-router-dom equivalents.

import {
  useNavigate,
  useLocation,
  useParams as useRouterParams,
  useSearchParams as useRouterSearchParams,
} from "react-router-dom"

/**
 * `useRouter()` — the Next 13+ app-router hook. Returns an object with
 * `push`, `replace`, `refresh`, `back`, `forward`. Each maps to a
 * react-router equivalent. `refresh` is a no-op (SPA has no server
 * rerender to trigger); `prefetch` is a no-op too.
 */
export function useRouter() {
  const nav = useNavigate()
  return {
    push: (path: string) => nav(path),
    replace: (path: string) => nav(path, { replace: true }),
    back: () => nav(-1),
    forward: () => nav(1),
    refresh: () => {},
    prefetch: (_path: string) => {},
  }
}

/** `usePathname()` — pathname only, no query, no hash. */
export function usePathname(): string {
  const location = useLocation()
  return location.pathname
}

/** `useSearchParams()` — Next returns ReadonlyURLSearchParams. */
export function useSearchParams(): URLSearchParams {
  const [params] = useRouterSearchParams()
  return params
}

/** `useParams()` — dynamic route params. Same shape in react-router. */
export function useParams<T extends Record<string, string | string[]>>(): T {
  return useRouterParams() as unknown as T
}

/** Server-only Next helpers — throw in SPA if called (they shouldn't be). */
export function redirect(_url: string): never {
  throw new Error("redirect() called in SPA — use useNavigate() instead")
}
export function permanentRedirect(_url: string): never {
  throw new Error("permanentRedirect() called in SPA — use useNavigate() instead")
}
export function notFound(): never {
  throw new Error("notFound() called in SPA — render a NotFound route instead")
}
